import { Children, useEffect, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";

import type { ClientViewState } from "./client-controller.js";
import { normalizeHostId } from "./discovery.js";
import { strings } from "./strings.js";
import type { PeerInfo, ShareState } from "./wire.js";

export type ShareStatusKind =
  | "idle"
  | "pending-approval"
  | "selecting"
  | "needs-gesture"
  | "active"
  | "error";

export interface ShareStatusPresentation {
  readonly kind: ShareStatusKind;
  readonly label: string;
  readonly detail?: string;
}

type ShareStatusInput = Pick<
  ClientViewState,
  | "captureMessage"
  | "captureRequestPending"
  | "localCaptureActive"
  | "needsCaptureGesture"
> & {
  readonly pickerSelecting?: boolean;
};

type SessionPhase = ClientViewState["signaling"]["phase"];

/** Keep browser-picker visibility as renderer-local presentation state. */
export async function withPickerSelecting<T>(
  capture: () => Promise<T>,
  setSelecting: (selecting: boolean) => void,
): Promise<T> {
  setSelecting(true);
  try {
    return await capture();
  } finally {
    setSelecting(false);
  }
}

export function shouldShowGenericConnectionBanner(
  phase: SessionPhase | undefined,
): boolean {
  return (
    phase === undefined ||
    phase === "idle" ||
    phase === "connecting" ||
    phase === "reconnecting" ||
    phase === "reconnect-escalated"
  );
}

export function recoverySurfaceOwnsPresentation(
  phase: SessionPhase | undefined,
): boolean {
  return (
    phase === "lna-denied" ||
    phase === "manual-reconnect" ||
    phase === "terminal-error" ||
    phase === "room-closed" ||
    phase === "stopped"
  );
}

export function videoEmptyStateMessage(
  transportMode: ClientViewState["signaling"]["transportMode"],
): string {
  return transportMode === "unreachable"
    ? strings.session.emptyUnreachable
    : strings.session.emptyVideo;
}

const INTERNAL_CAPTURE_MESSAGES: Readonly<Record<string, string>> = {
  admin: strings.session.share.captureEndedByBoard,
  "host-revoked": strings.session.share.captureEndedByBoard,
  "operator-stopped": strings.session.share.captureEndedByBoard,
  "peer-left": strings.session.share.captureEnded,
  "room-closed": strings.session.share.captureEndedWithRoom,
  superseded: strings.session.share.captureSuperseded,
  teardown: strings.session.share.captureEnded,
};

const TRUSTED_CAPTURE_PRESENTATIONS = new Set<string>([
  ...Object.values(INTERNAL_CAPTURE_MESSAGES),
  strings.errors.shareRequest.denied,
  strings.errors.shareRequest.busy,
  strings.errors.shareRequest.unsupportedDevice,
  strings.errors.shareRequest.alreadySharing,
  strings.errors.shareRequest.fallback,
  strings.errors.capture.retryGesture,
  strings.errors.capture.cancelled,
  strings.errors.capture.failed,
  ...[
    "NotFoundError",
    "NotReadableError",
    "OperationError",
    "OverconstrainedError",
    "SecurityError",
  ].map((errorName) => strings.errors.capture.failedWithName(errorName)),
]);

export function captureMessagePresentation(message: string): string {
  const internal = INTERNAL_CAPTURE_MESSAGES[message];
  if (internal !== undefined) return internal;
  return TRUSTED_CAPTURE_PRESENTATIONS.has(message)
    ? message
    : strings.session.share.captureUnavailable;
}

export function shareStatusPresentation(
  state: ShareStatusInput,
): ShareStatusPresentation {
  if (state.localCaptureActive) {
    return { kind: "active", label: strings.session.share.localActive };
  }
  if (state.pickerSelecting === true) {
    return {
      kind: "selecting",
      label: strings.session.share.selecting,
      detail: strings.session.share.selectingDetail,
    };
  }
  if (state.needsCaptureGesture) {
    return {
      kind: "needs-gesture",
      label: strings.session.share.needsSelection,
      detail: strings.session.share.needsSelectionDetail,
    };
  }
  if (state.captureRequestPending) {
    return {
      kind: "pending-approval",
      label: strings.session.share.awaitingApproval,
      detail: strings.session.share.awaitingApprovalDetail,
    };
  }
  if (state.captureMessage !== undefined) {
    return {
      kind: "error",
      label: strings.session.share.failed,
      detail: captureMessagePresentation(state.captureMessage),
    };
  }
  return { kind: "idle", label: strings.session.share.ready };
}

export function ShareStatusChip({
  state,
}: {
  readonly state: ShareStatusInput;
}): JSX.Element | null {
  const presentation = shareStatusPresentation(state);
  if (presentation.kind === "idle") return null;
  return (
    <span
      key={presentation.kind}
      className={`share-status-chip share-status-${presentation.kind}`}
      data-share-state={presentation.kind}
      role={presentation.kind === "error" ? "alert" : "status"}
    >
      <span className="share-status-dot" aria-hidden="true" />
      <strong>{presentation.label}</strong>
      {presentation.detail !== undefined && (
        <span className="share-status-detail">{presentation.detail}</span>
      )}
    </span>
  );
}

export function transportLabel(
  mode: "direct" | "relay" | "unreachable",
): string {
  return {
    direct: strings.session.transport.direct,
    relay: strings.session.transport.relay,
    unreachable: strings.session.transport.unreachable,
  }[mode];
}

export function TransportPill({
  mode,
}: {
  readonly mode: "direct" | "relay" | "unreachable";
}): JSX.Element {
  return (
    <span
      className={`transport ${mode}`}
      data-transport-mode={mode}
      role="status"
    >
      <span className="transport-dot" aria-hidden="true" />
      {transportLabel(mode)}
    </span>
  );
}

export function PlaneSwitch({
  active,
  disabled,
  onSwitch,
}: {
  readonly active: "video" | "whiteboard";
  readonly disabled: boolean;
  readonly onSwitch: (plane: "video" | "whiteboard") => void;
}): JSX.Element {
  return (
    <div
      className="plane-switch"
      data-plane-switch
      data-active-plane={active}
      role="group"
      aria-label={strings.session.planeSwitchAria}
    >
      <button
        type="button"
        data-plane="video"
        aria-pressed={active === "video"}
        aria-label={strings.session.videoAria}
        disabled={disabled}
        onClick={() => onSwitch("video")}
      >
        <span className="control-icon-label" data-control-icon-label>
          <ScreenIcon />
          <span className="control-label">{strings.session.video}</span>
        </span>
      </button>
      <button
        type="button"
        data-plane="whiteboard"
        aria-pressed={active === "whiteboard"}
        aria-label={strings.session.whiteboardAria}
        disabled={disabled}
        onClick={() => onSwitch("whiteboard")}
      >
        <span className="control-icon-label" data-control-icon-label>
          <WhiteboardIcon />
          <span className="control-label">{strings.session.whiteboard}</span>
        </span>
      </button>
    </div>
  );
}

export type OneBeatPresenceState = "closed" | "open" | "closing";
export type OneBeatPresenceAction = "show" | "hide" | "animation-end";

export function oneBeatPresenceStateAfter(
  state: OneBeatPresenceState,
  action: OneBeatPresenceAction,
): OneBeatPresenceState {
  if (action === "show") return "open";
  if (action === "hide") return state === "open" ? "closing" : state;
  return state === "closing" ? "closed" : state;
}

export function StatusRail({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const incoming = Children.toArray(children);
  const hasIncoming = incoming.length > 0;
  const heldChildrenRef = useRef<readonly ReactNode[]>(incoming);
  const [presence, setPresence] = useState<OneBeatPresenceState>(
    hasIncoming ? "open" : "closed",
  );

  useEffect(() => {
    if (hasIncoming) heldChildrenRef.current = incoming;
    setPresence((current) =>
      oneBeatPresenceStateAfter(current, hasIncoming ? "show" : "hide"),
    );
  }, [children, hasIncoming]);

  const renderedPresence = hasIncoming ? "open" : presence;
  const renderedChildren = hasIncoming
    ? incoming
    : renderedPresence === "closed"
      ? null
      : heldChildrenRef.current;

  return (
    <div
      className="status-rail"
      data-status-rail
      data-status-state={renderedPresence}
      aria-label={strings.session.statusRailAria}
      aria-hidden={renderedPresence === "closing" ? true : undefined}
      onAnimationEnd={(event) => {
        if (event.currentTarget !== event.target) return;
        if (
          event.animationName !== "session-rail-exit" &&
          event.animationName !== "session-rail-exit-reduced"
        )
          return;
        setPresence((current) =>
          oneBeatPresenceStateAfter(current, "animation-end"),
        );
      }}
    >
      {renderedChildren}
    </div>
  );
}

export function latchAutoFocusToastReady(
  alreadyReady: boolean,
  remoteReady: boolean,
): boolean {
  return alreadyReady || remoteReady;
}

export function activeShareNeedsAutoFocus({
  shareId,
  latchedShareId,
  remoteReady,
}: {
  readonly shareId: string;
  readonly latchedShareId?: string;
  readonly remoteReady: boolean;
}): boolean {
  return remoteReady && latchedShareId !== shareId;
}

export function latchManualPlaneChoice(
  currentShareId: string | undefined,
  share: ShareState | undefined,
): string | undefined {
  return share === undefined || share.status === "idle"
    ? currentShareId
    : share.shareId;
}

export function presenterShareToastMessage(
  presenterName?: string,
  presenterIsHost = false,
): string {
  return presenterIsHost
    ? strings.session.hostPresenterStarted
    : strings.session.presenterStarted(presenterName);
}

export function AutoFocusToast({
  presenterName,
  presenterIsHost = false,
  remoteReady,
}: {
  readonly presenterName?: string;
  readonly presenterIsHost?: boolean;
  readonly remoteReady: boolean;
}): JSX.Element | null {
  const readyRef = useRef(false);
  readyRef.current = latchAutoFocusToastReady(readyRef.current, remoteReady);

  if (!readyRef.current) return null;

  return (
    <div
      className="auto-focus-toast auto-focus-toast-ready"
      data-auto-focus-toast
      role="status"
    >
      {presenterShareToastMessage(presenterName, presenterIsHost)}
    </div>
  );
}

export function PresenterNameplate({
  presenterName,
}: {
  readonly presenterName?: string;
}): JSX.Element {
  const normalizedPresenterName = presenterName?.normalize("NFC");
  const displayName =
    normalizedPresenterName ?? strings.session.presenterFallback;
  const nameplate = strings.session.presenterNameplate(normalizedPresenterName);
  const displayNameOffset = nameplate.lastIndexOf(displayName);
  return (
    <div className="presenter-nameplate" data-presenter-nameplate role="status">
      {displayNameOffset < 0 ? (
        nameplate
      ) : (
        <>
          {nameplate.slice(0, displayNameOffset)}
          <strong>{displayName}</strong>
          {nameplate.slice(displayNameOffset + displayName.length)}
        </>
      )}
    </div>
  );
}

export function BoardContext({
  hostId,
}: {
  readonly hostId: string;
}): JSX.Element {
  const context = strings.brand.boardContext(hostId);
  const hostIdOffset = context.lastIndexOf(hostId);
  return (
    <span>
      {hostIdOffset < 0 ? (
        context
      ) : (
        <>
          {context.slice(0, hostIdOffset)}
          <strong>{hostId}</strong>
          {context.slice(hostIdOffset + hostId.length)}
        </>
      )}
    </span>
  );
}

export function RoomStageSkeleton({
  visible = true,
}: {
  readonly visible?: boolean;
} = {}): JSX.Element | null {
  const [presence, setPresence] = useState<OneBeatPresenceState>(
    visible ? "open" : "closed",
  );

  useEffect(() => {
    setPresence((current) =>
      oneBeatPresenceStateAfter(current, visible ? "show" : "hide"),
    );
  }, [visible]);

  const renderedPresence = visible ? "open" : presence;
  if (renderedPresence === "closed") return null;

  return (
    <div
      className="session-stage-skeleton"
      data-room-skeleton
      data-skeleton-state={renderedPresence}
      role="status"
      aria-label={strings.session.stageLoadingAria}
      aria-hidden={renderedPresence === "closing" ? true : undefined}
      onAnimationEnd={(event) => {
        if (event.currentTarget !== event.target) return;
        if (
          event.animationName !== "session-skeleton-exit" &&
          event.animationName !== "session-skeleton-exit-reduced"
        )
          return;
        setPresence((current) =>
          oneBeatPresenceStateAfter(current, "animation-end"),
        );
      }}
    >
      <span className="session-skeleton-block session-skeleton-wide" />
      <span className="session-skeleton-block" />
      <span className="session-skeleton-block session-skeleton-short" />
    </div>
  );
}

export type MediaOverlayKind =
  | "presenter-reconnecting"
  | "display-recovering"
  | "track-muted";

export function mediaOverlayPresentation({
  hostMediaRecovering,
  presenterRecovering,
  remoteTrackMuted,
}: {
  readonly hostMediaRecovering: boolean;
  readonly presenterRecovering: boolean;
  readonly remoteTrackMuted: boolean;
}): { readonly kind: MediaOverlayKind; readonly message: string } | undefined {
  if (presenterRecovering) {
    return {
      kind: "presenter-reconnecting",
      message: strings.session.media.presenterReconnecting,
    };
  }
  if (hostMediaRecovering) {
    return {
      kind: "display-recovering",
      message: strings.session.media.displayRecovering,
    };
  }
  if (remoteTrackMuted) {
    return {
      kind: "track-muted",
      message: strings.session.media.trackMuted,
    };
  }
  return undefined;
}

export type RosterSheetState = "closed" | "open" | "closing";
export type RosterSheetAction = "toggle" | "close" | "animation-end";

export function rosterSheetStateAfter(
  state: RosterSheetState,
  action: RosterSheetAction,
): RosterSheetState {
  if (action === "toggle") return state === "open" ? "closing" : "open";
  if (action === "close") return state === "open" ? "closing" : state;
  return state === "closing" ? "closed" : state;
}

export function RosterSheet({
  peers,
  presenterClientId,
  roomResolved,
  selfClientId,
  verifiedHostId,
}: {
  readonly peers: readonly PeerInfo[];
  readonly presenterClientId?: string;
  readonly roomResolved: boolean;
  readonly selfClientId?: string;
  readonly verifiedHostId?: string;
}): JSX.Element {
  const [sheetState, setSheetState] = useState<RosterSheetState>("closed");
  const orderedPeers = [...peers].sort((left, right) => {
    if (left.clientId === "host") return -1;
    if (right.clientId === "host") return 1;
    const leftPresenter = left.clientId === presenterClientId;
    const rightPresenter = right.clientId === presenterClientId;
    if (leftPresenter !== rightPresenter) return leftPresenter ? -1 : 1;
    return 0;
  });
  const otherParticipantCount = peers.filter(
    (peer) => peer.clientId !== "host" && peer.clientId !== selfClientId,
  ).length;
  return (
    <details
      className="roster-sheet"
      data-roster-state={sheetState}
      open={sheetState !== "closed"}
    >
      <summary
        data-roster-toggle
        aria-label={strings.session.roster.summaryAria(peers.length)}
        onClick={(event) => {
          event.preventDefault();
          setSheetState((current) => rosterSheetStateAfter(current, "toggle"));
        }}
      >
        <span className="control-icon-label" data-control-icon-label>
          <ParticipantsIcon />
          <span className="roster-label-wide">
            {strings.session.roster.count(peers.length)}
          </span>
          <span className="roster-more-icon" aria-hidden="true">
            ⋯
          </span>
          <span className="roster-label-narrow">
            {strings.session.roster.more}
          </span>
          <ChevronDownIcon />
        </span>
      </summary>
      <button
        type="button"
        className="roster-backdrop"
        data-roster-backdrop
        aria-label={strings.session.roster.closeAria}
        onClick={() =>
          setSheetState((current) => rosterSheetStateAfter(current, "close"))
        }
      />
      <aside
        className="card roster roster-panel"
        data-roster-sheet
        aria-labelledby="participants-heading"
        tabIndex={0}
        onAnimationEnd={(event) => {
          if (
            event.animationName !== "session-roster-exit" &&
            event.animationName !== "session-roster-exit-reduced"
          )
            return;
          setSheetState((current) =>
            rosterSheetStateAfter(current, "animation-end"),
          );
        }}
      >
        <h2 id="participants-heading">
          {strings.session.roster.count(peers.length)}
        </h2>
        {!roomResolved && (
          <div className="roster-loading" role="status">
            <span>{strings.session.roster.loading}</span>
            <span className="roster-skeleton-row" aria-hidden="true" />
            <span className="roster-skeleton-row" aria-hidden="true" />
          </div>
        )}
        {roomResolved && otherParticipantCount === 0 && (
          <p>{strings.session.roster.empty}</p>
        )}
        <ul>
          {orderedPeers.map((peer) => {
            const displayName = sessionPeerDisplayName(peer);
            return (
              <li key={peer.clientId}>
                <span className="avatar" aria-hidden="true">
                  {[...displayName][0] ?? "?"}
                </span>
                <span>
                  <strong>{displayName}</strong>
                  <span className="roster-badges">
                    <small>
                      {peer.clientId === "host"
                        ? strings.session.roster.board
                        : peer.connection === "zombie"
                          ? strings.session.roster.reconnecting
                          : strings.session.roster.connected}
                    </small>
                    {verifiedBoardBadge(peer, verifiedHostId) !== undefined && (
                      <small className="verified-board-badge">
                        {verifiedBoardBadge(peer, verifiedHostId)}
                      </small>
                    )}
                    {peer.clientId === presenterClientId && (
                      <small className="presenter-badge">
                        {strings.session.roster.presenting}
                      </small>
                    )}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </aside>
    </details>
  );
}

export function verifiedBoardBadge(
  peer: Pick<PeerInfo, "clientId" | "verifiedBoard">,
  hostId: string | undefined,
): string | undefined {
  if (
    peer.clientId !== "host" ||
    peer.verifiedBoard !== true ||
    hostId === undefined
  )
    return undefined;
  try {
    // Bind only the server-asserted licensed host member to the per-host TLS
    // target. A free-form displayName never participates in this decision.
    return strings.session.roster.verifiedBoard(normalizeHostId(hostId));
  } catch {
    return undefined;
  }
}

export function sessionPeerDisplayName(
  peer: Pick<PeerInfo, "clientId" | "displayName">,
): string {
  return peer.clientId === "host"
    ? strings.session.hostDisplayName
    : peer.displayName.normalize("NFC");
}

export type StopShareConfirmAction = "arm" | "cancel" | "confirm";

export function stopShareConfirmState(action: StopShareConfirmAction): boolean {
  return action === "arm";
}

export function StopShareConfirm({
  onStopShare,
}: {
  readonly onStopShare: () => void;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        className="secondary danger stop-share-control"
        data-session-action="stop-share"
        aria-haspopup="true"
        aria-expanded="false"
        onClick={() => setConfirming(stopShareConfirmState("arm"))}
      >
        {strings.session.share.stop}
      </button>
    );
  }

  return (
    <span
      className="stop-share-confirm"
      data-stop-share-confirm
      role="group"
      aria-label={strings.session.share.stopConfirmAria}
    >
      <span>{strings.session.share.stopQuestion}</span>
      <button
        type="button"
        className="danger"
        data-session-action="confirm-stop-share"
        onClick={() => {
          setConfirming(stopShareConfirmState("confirm"));
          onStopShare();
        }}
      >
        {strings.session.share.confirmStop}
      </button>
      <button
        type="button"
        className="secondary"
        data-session-action="cancel-stop-share"
        onClick={() => setConfirming(stopShareConfirmState("cancel"))}
      >
        {strings.session.share.continueSharing}
      </button>
    </span>
  );
}

export function BottomBar({
  activePlane,
  localCaptureActive,
  needsCaptureGesture,
  onLeave,
  onRequestShare,
  onStartCapture,
  onStopShare,
  onSwitchPlane,
  peers,
  pickerSelecting,
  presenterClientId,
  requestShareDisabled,
  roomResolved,
  selfClientId,
  shareState,
  showShareRequest,
  verifiedHostId,
}: {
  readonly activePlane: "video" | "whiteboard";
  readonly localCaptureActive: boolean;
  readonly needsCaptureGesture: boolean;
  readonly onLeave: () => void;
  readonly onRequestShare: () => void;
  readonly onStartCapture: () => void;
  readonly onStopShare: () => void;
  readonly onSwitchPlane: (plane: "video" | "whiteboard") => void;
  readonly peers: readonly PeerInfo[];
  readonly pickerSelecting: boolean;
  readonly presenterClientId?: string;
  readonly requestShareDisabled: boolean;
  readonly roomResolved: boolean;
  readonly selfClientId?: string;
  readonly shareState: ShareStatusInput;
  readonly showShareRequest: boolean;
  readonly verifiedHostId?: string;
}): JSX.Element {
  const renderedShareState = { ...shareState, pickerSelecting };
  const criticalNotice =
    shareStatusPresentation(renderedShareState).kind === "error";
  return (
    <footer
      className={`bottom-bar${localCaptureActive ? " bottom-bar-sharing" : ""}${criticalNotice ? " bottom-bar-critical-notice" : ""}`}
      data-session-control-bar
      data-sharing={localCaptureActive ? "true" : "false"}
      aria-label={strings.session.share.controlsAria}
    >
      <PlaneSwitch
        active={activePlane}
        disabled={requestShareDisabled}
        onSwitch={onSwitchPlane}
      />
      <div className="share-control-slot">
        <ShareStatusChip state={renderedShareState} />
        <div className="share-action-row">
          {showShareRequest && (
            <button
              type="button"
              className="share-primary"
              data-session-action="request-share"
              aria-label={strings.session.share.myScreenAria}
              onClick={onRequestShare}
              disabled={requestShareDisabled}
            >
              <span className="control-icon-label" data-control-icon-label>
                <ScreenIcon />
                <span className="control-label">
                  {strings.session.share.myScreen}
                </span>
              </span>
            </button>
          )}
          {needsCaptureGesture && !pickerSelecting && (
            <button
              type="button"
              className="share-primary"
              data-session-action="start-capture"
              aria-label={strings.session.share.startAria}
              onClick={onStartCapture}
              disabled={requestShareDisabled}
            >
              <span className="control-icon-label" data-control-icon-label>
                <ScreenIcon />
                <span className="control-label">
                  {strings.session.share.start}
                </span>
              </span>
            </button>
          )}
          {pickerSelecting && (
            <button
              type="button"
              className="share-primary share-selecting"
              data-session-action="selecting-capture"
              aria-label={strings.session.share.selectingAria}
              disabled
            >
              <span className="control-icon-label" data-control-icon-label>
                <ScreenIcon />
                <span className="control-label">
                  {strings.session.share.selecting}
                </span>
              </span>
            </button>
          )}
          {(shareState.captureRequestPending || needsCaptureGesture) && (
            <button
              type="button"
              className="secondary share-cancel"
              data-session-action="cancel-share"
              onClick={onStopShare}
            >
              {strings.session.share.cancelRequest}
            </button>
          )}
          {localCaptureActive && <StopShareConfirm onStopShare={onStopShare} />}
        </div>
      </div>
      <RosterSheet
        peers={peers}
        presenterClientId={presenterClientId}
        roomResolved={roomResolved}
        selfClientId={selfClientId}
        verifiedHostId={verifiedHostId}
      />
      <button
        type="button"
        className="leave-control"
        data-session-action="leave"
        aria-label={strings.session.share.leaveAria}
        onClick={onLeave}
      >
        <span className="control-icon-label" data-control-icon-label>
          <LeaveIcon />
          <span className="control-label">{strings.session.share.leave}</span>
        </span>
      </button>
    </footer>
  );
}

export function MaximizeToggle({
  maximized,
  onToggle,
  enterAria = strings.session.maximize.enterAria,
}: {
  readonly maximized: boolean;
  readonly onToggle: () => void;
  readonly enterAria?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className="plane-max-toggle"
      data-plane-max-toggle
      aria-pressed={maximized}
      aria-label={maximized ? strings.session.maximize.exitAria : enterAria}
      onClick={onToggle}
    >
      {maximized ? <MinimizeIcon /> : <MaximizeIcon />}
    </button>
  );
}

interface SessionIconProps {
  readonly className?: string;
}

// Fullscreen/maximize corner-arrows glyph on the KlassMate 24px icon grid.
export function MaximizeIcon({
  className = "session-icon",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

// Inward corner-arrows glyph paired with MaximizeIcon for the exit affordance.
export function MinimizeIcon({
  className = "session-icon",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
  );
}

// Canonical KlassMate source: packages/ui/src/icons/custom/v1/V1ClassroomDeviceIcon.tsx
export function ScreenIcon({
  className = "session-icon",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.5 2.5H2.5C2.16848 2.5 1.85054 2.6317 1.61612 2.86612C1.3817 3.10054 1.25 3.41848 1.25 3.75V13.75C1.25 14.0815 1.3817 14.3995 1.61612 14.6339C1.85054 14.8683 2.16848 15 2.5 15H7.5V17.5H5V18.75H15V17.5H12.5V15H17.5C17.8315 15 18.1495 14.8683 18.3839 14.6339C18.6183 14.3995 18.75 14.0815 18.75 13.75V3.75C18.75 3.41848 18.6183 3.10054 18.3839 2.86612C18.1495 2.6317 17.8315 2.5 17.5 2.5ZM11.25 17.5H8.75V15H11.25V17.5ZM17.5 13.75H2.5V3.75H17.5V13.75Z" />
    </svg>
  );
}

// Canonical KlassMate source: packages/ui/src/icons/custom/PenIcon.tsx
export function WhiteboardIcon({
  className = "session-icon",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 20 22"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.316665 3.26595L2.96667 5.62294C3.17778 5.93234 3.48889 6.11111 3.81667 6.11111L16.1722 6.11111C16.5 6.11111 16.8111 5.93234 17.0222 5.62294L19.6722 3.26595C19.8833 3.00468 20 2.66089 20 2.29648L20 0L0 -1.74846e-06L-2.00164e-07 2.2896C-2.32021e-07 2.65401 0.116668 3.00467 0.327779 3.25907L0.316665 3.26595ZM4.79167 7.33333L15.2083 7.33333C15.7836 7.33333 16.25 7.88054 16.25 8.55556L16.25 9.77778C16.25 10.4528 15.7836 11 15.2083 11L4.79167 11C4.21637 11 3.75 10.4528 3.75 9.77778L3.75 8.55555C3.75 7.88054 4.21637 7.33333 4.79167 7.33333ZM5 12.2222L14.9998 12.2222L15 17.1973C15 19.8484 12.76 22 10.0001 22C7.24016 22 5.00021 19.8484 5.00021 17.1973L5 12.2222Z"
      />
    </svg>
  );
}

// Canonical KlassMate source: klassmate-teacher-react/src/assets/icons/menu-user.svg (inner path only; mask removed)
export function ParticipantsIcon({
  className = "session-icon",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.38893 2.42133C8.9778 2.14662 8.49445 2 8 2C7.33696 2 6.70108 2.26339 6.23223 2.73223C5.76339 3.20107 5.5 3.83696 5.5 4.5C5.5 4.99445 5.64662 5.4778 5.92133 5.88893C6.19603 6.30005 6.58648 6.62048 7.04329 6.8097C7.50011 6.99892 8.00277 7.04843 8.48773 6.95196C8.97268 6.8555 9.41814 6.6174 9.76777 6.26777C10.1174 5.91814 10.3555 5.47268 10.452 4.98773C10.5484 4.50277 10.4989 4.00011 10.3097 3.54329C10.1205 3.08648 9.80005 2.69603 9.38893 2.42133ZM6.05551 1.58986C6.63108 1.20527 7.30777 1 8 1C8.92826 1 9.8185 1.36875 10.4749 2.02513C11.1313 2.6815 11.5 3.57174 11.5 4.5C11.5 5.19223 11.2947 5.86892 10.9101 6.4445C10.5256 7.02007 9.97893 7.46867 9.33939 7.73358C8.69985 7.99848 7.99612 8.0678 7.31719 7.93275C6.63825 7.7977 6.01461 7.46436 5.52513 6.97487C5.03564 6.48539 4.7023 5.86175 4.56725 5.18282C4.4322 4.50388 4.50152 3.80015 4.76642 3.16061C5.03133 2.52107 5.47993 1.97444 6.05551 1.58986ZM12 15H13V12.5C13 11.5717 12.6313 10.6815 11.9749 10.0251C11.3185 9.36875 10.4283 9 9.5 9H6.5C5.57174 9 4.6815 9.36875 4.02513 10.0251C3.36875 10.6815 3 11.5717 3 12.5V15H4V12.5C4 11.837 4.26339 11.2011 4.73223 10.7322C5.20107 10.2634 5.83696 10 6.5 10H9.5C9.8283 10 10.1534 10.0647 10.4567 10.1903C10.76 10.3159 11.0356 10.5001 11.2678 10.7322C11.4999 10.9644 11.6841 11.24 11.8097 11.5433C11.9353 11.8466 12 12.1717 12 12.5V15Z"
      />
    </svg>
  );
}

// Canonical KlassMate source: packages/ui/src/icons/custom/v1/V1ClassroomLogoutIcon.tsx
export function LeaveIcon({
  className = "session-icon",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 52 52"
      fill="none"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M30.5 14V11C30.5 9.89543 29.6046 9 28.5 9H14C12.8954 9 12 9.89543 12 11V41C12 42.1046 12.8954 43 14 43H28.5C29.6046 43 30.5 42.1046 30.5 41V37.5"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path d="M18.5 26H40" stroke="currentColor" strokeWidth="3" />
      <path d="M33 19L40 26L33 33" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}

// Canonical KlassMate source: packages/ui/src/icons/custom/v1/V1ClassroomChevronDownIcon.tsx
export function ChevronDownIcon({
  className = "session-icon roster-chevron",
}: SessionIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 17 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8.5 10.9998L3.5 5.9998L4.2 5.2998L8.5 9.59981L12.8 5.2998L13.5 5.9998L8.5 10.9998Z" />
    </svg>
  );
}
